import React, { useMemo } from "react";
import { UsersThree, ArrowRight, Crown, Briefcase, Target, Wrench, Sparkle } from "@phosphor-icons/react";
import { ROLE_BY_ID, ROLE_REGISTRY } from "../lib/rbac";
import { useTeam, useCurrentOperative } from "../lib/teamStore";
import { computeLeadershipPdi } from "../lib/team";

/* ─────────────────────────────────────────────────────────────────────────
   Team Pulse Card — slotted into the Common Operating Picture.
   Surfaces Leadership PDI, the acting operative, and the 4-tier hierarchy.
   Live from MongoDB via the team store — survives refresh + sessions.
   Strict palette: Alabaster #FAF9F6 · Deep Slate #0F172A · Executive Green
   #0D9488 (pass/success only).
   ─────────────────────────────────────────────────────────────────────── */

const ALABASTER = "#FAF9F6";
const DEEP_SLATE = "#0F172A";
const EX_GREEN   = "#0D9488";
const RULE       = "#E5E7EB";
const SUBTEXT    = "#334155";
const AMBER      = "#92400E";

const ROLE_GLYPH = { DIRECTOR: Crown, MANAGER: Briefcase, CATEGORY_LEADER: Target, BUYER: Wrench };

export default function TeamPulseCard({ onOpenTeam }) {
  const { operative } = useCurrentOperative();
  const { operatives: team, loaded } = useTeam();
  const leadership = useMemo(() => computeLeadershipPdi(team), [team]);

  const tierBuckets = useMemo(() => {
    const b = { DIRECTOR: 0, MANAGER: 0, CATEGORY_LEADER: 0, BUYER: 0 };
    team.forEach((u) => { if (b[u.role] !== undefined) b[u.role] += 1; });
    return b;
  }, [team]);

  const actingRole = ROLE_BY_ID[operative.role] || ROLE_REGISTRY[3];
  const ActingGlyph = ROLE_GLYPH[operative.role] || Wrench;

  const tierColor =
    leadership.tier === "GREEN"  ? EX_GREEN
    : leadership.tier === "YELLOW" ? AMBER
    :                              "#7C2D12";

  return (
    <section
      data-testid="cop-team-pulse-card"
      className="rounded-sm border overflow-hidden"
      style={{ background: ALABASTER, borderColor: RULE, color: DEEP_SLATE }}
    >
      {/* Header strip */}
      <header
        className="px-5 py-3 border-b flex items-center justify-between gap-4 flex-wrap"
        style={{ borderColor: RULE, background: "#F3F4F6" }}
      >
        <div className="flex items-center gap-2">
          <UsersThree weight="duotone" size={16} style={{ color: DEEP_SLATE }} />
          <span className="font-display font-bold text-[13px] tracking-tight" style={{ color: DEEP_SLATE }}>
            Team Pulse · 4-Tier Hierarchy
          </span>
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase font-bold ml-2" style={{ color: SUBTEXT }}>
            integrated into the Common Operating Picture
          </span>
        </div>
        <button
          type="button"
          onClick={onOpenTeam}
          data-testid="cop-open-team"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm font-mono text-[10.5px] tracking-[0.16em] uppercase font-bold transition-colors"
          style={{ background: DEEP_SLATE, color: ALABASTER, border: `1px solid ${DEEP_SLATE}` }}
        >
          Open Team Management <ArrowRight weight="bold" size={11} />
        </button>
      </header>

      {/* 4-column boardroom strip */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-px" style={{ background: RULE }}>
        {/* Acting operative */}
        <div
          className="md:col-span-3 p-5"
          style={{ background: "#FFFFFF" }}
          data-testid="cop-acting-operative"
        >
          <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2" style={{ color: SUBTEXT }}>
            Acting Operative
          </div>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="inline-flex items-center justify-center h-10 w-10 rounded-sm font-mono text-[12px] font-bold shrink-0"
              style={{ background: DEEP_SLATE, color: ALABASTER, letterSpacing: "0.05em" }}
            >
              {operative.name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("")}
            </span>
            <div className="min-w-0">
              <div className="font-display font-bold text-[14px] tracking-tight" style={{ color: DEEP_SLATE }}>
                {operative.name}
              </div>
              <div className="text-[10.5px] font-mono inline-flex items-center gap-1 mt-0.5"
                   style={{ color: SUBTEXT }}>
                <ActingGlyph weight="bold" size={11} /> {actingRole.label} · {actingRole.sub}
              </div>
            </div>
          </div>
        </div>

        {/* Leadership PDI */}
        <div
          className="md:col-span-3 p-5 flex flex-col"
          style={{ background: "#FFFFFF" }}
          data-testid="cop-leadership-pdi"
        >
          <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold flex items-center gap-1.5"
               style={{ color: EX_GREEN }}>
            <Sparkle weight="fill" size={11} /> Leadership PDI · {leadership.tier}
          </div>
          <div
            data-testid="cop-leadership-pdi-score"
            className="font-display font-extrabold leading-none mt-1 text-[40px] tabular-nums tracking-tighter"
            style={{ color: tierColor }}
          >
            {leadership.score}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.16em] mt-1.5 font-bold" style={{ color: SUBTEXT }}>
            avg. alignment &amp; WISDOM
          </div>
        </div>

        {/* Coverage */}
        <div
          className="md:col-span-3 p-5"
          style={{ background: "#FFFFFF" }}
          data-testid="cop-focus-coverage"
        >
          <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold" style={{ color: SUBTEXT }}>
            Category Focus Coverage
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span
              className="font-display font-extrabold text-[28px] tabular-nums tracking-tight"
              style={{ color: leadership.coverage === 100 ? EX_GREEN : DEEP_SLATE }}
            >
              {leadership.coverage}%
            </span>
          </div>
          <div className="mt-3 h-1.5 rounded-sm w-full overflow-hidden" style={{ background: "#E5E7EB" }}>
            <div
              className="h-full"
              style={{
                width: `${leadership.coverage}%`,
                background: leadership.coverage === 100 ? EX_GREEN : DEEP_SLATE,
                transition: "width 280ms ease",
              }}
            />
          </div>
          <div className="text-[10.5px] font-mono mt-2 tabular-nums" style={{ color: SUBTEXT }}>
            {leadership.focusMissing > 0 ? (
              <span style={{ color: AMBER, fontWeight: 700 }}>
                {leadership.focusMissing}
              </span>
            ) : (
              <span style={{ color: EX_GREEN, fontWeight: 700 }}>0</span>
            )}{" "}of {leadership.focusEligible || 0} need a category focus
          </div>
        </div>

        {/* Hierarchy distribution mini bars */}
        <div
          className="md:col-span-3 p-5"
          style={{ background: "#FFFFFF" }}
          data-testid="cop-hierarchy-mini"
        >
          <div className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2" style={{ color: SUBTEXT }}>
            Hierarchy · {team.length} operatives
          </div>
          <div className="space-y-1.5">
            {ROLE_REGISTRY.map((r) => {
              const count = tierBuckets[r.id] || 0;
              const pct   = team.length ? Math.round((count / team.length) * 100) : 0;
              const G     = ROLE_GLYPH[r.id];
              return (
                <div key={r.id} data-testid={`cop-hierarchy-${r.id.toLowerCase()}`}>
                  <div className="flex items-center gap-2">
                    <G weight="bold" size={10} style={{ color: DEEP_SLATE }} />
                    <span className="font-mono text-[10.5px] font-bold" style={{ color: DEEP_SLATE }}>
                      {r.label}
                    </span>
                    <span className="ml-auto font-mono text-[10.5px] font-bold tabular-nums" style={{ color: SUBTEXT }}>
                      {count}
                    </span>
                  </div>
                  <div className="h-[4px] rounded-sm overflow-hidden mt-0.5" style={{ background: "#E5E7EB" }}>
                    <div className="h-full" style={{ width: `${pct}%`, background: DEEP_SLATE }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
